import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea"; // Import Textarea
import { toast } from "@/hooks/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TablesInsert } from '@/integrations/supabase/types';

// Define the Zod schema for the contact form
const contactFormSchema = z.object({
  first_name: z.string().min(1, { message: "First name is required." }),
  last_name: z.string().min(1, { message: "Last name is required." }),
  email: z.string().email({ message: "Invalid email address." }),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }),
});

// Infer the type from the schema
export type ContactFormValues = z.infer<typeof contactFormSchema>;

const Contact = () => {
  const [loading, setLoading] = useState(false);

  // Initialize react-hook-form
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      message: "",
    },
  });

  // Handle form submission
  const onSubmit = async (values: ContactFormValues) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_form_submissions')
        .insert<TablesInsert<'contact_form_submissions'>>([
          {
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            message: values.message,
          },
        ])
        .select();

      if (error) {
        console.error('Error inserting data:', error);
        toast.error("Failed to send message.", { description: error.message, businessName: "Contact Form" });
      } else {
        console.log('Data inserted successfully:', data);
        toast.success("Message sent successfully!", { businessName: "Contact Form" });
        form.reset(); // Reset form fields
      }
    } catch (error: unknown) {
      console.error('Catch Error:', error);
      const message = error instanceof Error ? error.message : "An unexpected error occurred.";
      toast.error("An error occurred.", { description: message, businessName: "Contact Form" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-white dark:bg-background">
      <Navbar />
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-primary/10 to-background py-12 dark:from-primary/20 dark:to-background">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-4xl">
                Contact Us
              </h1>
              <p className="mt-4 text-lg leading-8 text-gray-600 dark:text-gray-400">
                We'd love to hear from you! Get in touch using the form below.
              </p>
            </div>
            <div className="mt-10 mx-auto max-w-md">
              {/* Use the Form component from shadcn/ui */}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                    {/* First Name */}
                    <div className="sm:col-span-3">
                      <FormField
                        control={form.control}
                        name="first_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First name</FormLabel>
                            <FormControl>
                              <Input placeholder="John" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Last Name */}
                    <div className="sm:col-span-3">
                      <FormField
                        control={form.control}
                        name="last_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last name</FormLabel>
                            <FormControl>
                              <Input placeholder="Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Email */}
                    <div className="sm:col-span-6">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email address</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="your@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Message */}
                    <div className="sm:col-span-6">
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Your message here..."
                                className="resize-none" // Optional: prevent resizing
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Policy Links (Optional - kept for consistency) */}
                  <div className="space-y-2">
                     <div className="flex items-center gap-x-2">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.834 1 1 0 00-1.995.185l-.5-.5A1 1 0 005 12h2a1 1 0 00.966-.741l.343.342 2 2a1 1 0 001.414 0l4-4a1 1 0 00-.707-1.707L13 5.793A2 2 2 0 0010.867 7z" clipRule="evenodd" />
                        </svg>
                        <p className="text-xs leading-5 text-gray-500">
                          By submitting this form, you agree to our <a href="#" className="font-semibold text-brand-600 hover:text-brand-500">terms and conditions</a>.
                        </p>
                      </div>
                      {/* Add other policy links similarly if needed */}
                  </div>

                  {/* Submit Button */}
                  <div className="flex items-center justify-end gap-x-6">
                     {/* Optional: Add a cancel button if needed */}
                     {/* <Button type="button" variant="ghost">Cancel</Button> */}
                    <Button type="submit" disabled={loading}>
                      {loading ? "Sending..." : "Send Message"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
